import type { components } from '@multaqa/api-client'
import { useQuery } from '@tanstack/react-query'
import { api } from './api'
import { meQuery } from './me'

export type TaxRate = components['schemas']['TaxRateResource']

/**
 * A rate's code for a summary row. Until the rates arrive, or for a rate the
 * list no longer has, a dash: never the raw id (screen quality: no raw codes).
 */
export function taxRateCode(taxRates: Pick<TaxRate, 'id' | 'code'>[], taxRateId: string): string {
  return taxRates.find((rate) => rate.id === taxRateId)?.code ?? '—'
}

/**
 * The active company's tax rates, one query and one cache entry for every
 * screen that lists them.
 *
 * Who may read them is the API's rule (`TaxRatePolicy::viewAny`:
 * `accounting_tax.edit` or `accounting_report.view`), but the screens that read
 * them gate on different things (a module flag, a document permission, nothing),
 * so the hook does not decide. A caller that may not read passes `enabled: false`.
 * `select` gives a screen its own filter; the cache keeps the full list.
 */
export function useCompanyTaxRates<TSelected = TaxRate[]>({
  enabled = true,
  select,
}: {
  enabled?: boolean
  select?: (rates: TaxRate[]) => TSelected
} = {}) {
  const { data: me } = useQuery(meQuery)
  const companyId = me?.active_company_id ?? null

  return useQuery<TaxRate[], Error, TSelected>({
    queryKey: ['companies', companyId, 'accounting', 'tax-rates'] as const,
    enabled: Boolean(companyId && enabled),
    queryFn: async (): Promise<TaxRate[]> => {
      if (!companyId) return []
      const response = await api.GET('/accounting/tax-rates', {
        headers: { 'X-Company-Id': companyId },
      })
      if (response.error) throw new Error(response.error.message ?? 'Failed to load tax rates')
      return response.data?.data ?? []
    },
    select,
  })
}
